import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Verified, Filter, X, ChevronLeft, ChevronRight, Map, List, Star, Navigation, Target, TrendingUp, ArrowUpDown, Globe, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '@/components/Navbar';
import MapView from '@/components/MapView';
import FavoriteButton from '@/components/FavoriteButton';
import { BadgeDisplay } from '@/components/CommunityVoting';
import AdvancedFilters from '@/components/AdvancedFilters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const PLACEHOLDER_IMAGES = [
  "https://images.pexels.com/photos/10375895/pexels-photo-10375895.jpeg",
  "https://images.unsplash.com/photo-1569598525846-cc400d20fac4?w=800",
  "https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg",
  "https://images.unsplash.com/photo-1709044472172-9b5af8b4491e?w=800"
];

export default function BusinessListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [featuredOnline, setFeaturedOnline] = useState([]);
  const [forYouBusinesses, setForYouBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [businessType, setBusinessType] = useState('all'); // all, local, online
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({});

  // Fetch "For You" recommendations on mount
  useEffect(() => {
    const fetchForYou = async () => {
      try {
        const response = await fetch(`${API}/for-you`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.personalized && data.businesses?.length > 0) {
            setForYouBusinesses(data.businesses);
          }
        }
      } catch (e) {
        // Silent fail
      }
    };
    fetchForYou();
  }, []);

  // Request user location on mount — state detection now handled by backend
  useEffect(() => {
    if (navigator.geolocation && businessType !== 'online') {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => { console.log('Location not available:', error); },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const catRes = await fetch(`${API}/categories`);
        if (catRes.ok) {
          const cats = await catRes.json();
          setCategories(cats);
        }

        // Build query params
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedCategory) params.append('category', selectedCategory);
        params.append('business_type', businessType);
        params.append('page', searchParams.get('page') || '1');

        // Add advanced filters
        if (advancedFilters.price_range) params.append('price_range', advancedFilters.price_range);
        if (advancedFilters.min_rating) params.append('min_rating', advancedFilters.min_rating);
        if (advancedFilters.open_now) params.append('open_now', 'true');
        if (advancedFilters.verified_only) params.append('verified_only', 'true');
        if (advancedFilters.has_reviews) params.append('has_reviews', 'true');
        if (advancedFilters.year_established_min) params.append('year_established_min', advancedFilters.year_established_min);
        if (advancedFilters.year_established_max) params.append('year_established_max', advancedFilters.year_established_max);

        // When location is available, always sort nearest-first.
        // User can override with an explicit sort selection.
        if (userLocation && businessType !== 'online') {
          params.append('latitude', userLocation.lat.toString());
          params.append('longitude', userLocation.lng.toString());
          // Only override with explicit user sort if they actually changed it
          if (sortBy !== 'default') params.append('sort', sortBy);
        } else if (sortBy !== 'default') {
          params.append('sort', sortBy);
        }

        // Track search for trending
        if (searchQuery) {
          fetch(`${API}/trending/track?query=${encodeURIComponent(searchQuery)}`, { method: 'POST' });
        }

        // Fetch businesses
        const bizRes = await fetch(`${API}/businesses?${params.toString()}`);
        if (bizRes.ok) {
          const data = await bizRes.json();
          setBusinesses(data.businesses);
          setTopPicks(data.top_picks || []);
          setFeaturedOnline(data.featured_online || []);
          setPagination({ page: data.page, pages: data.pages, total: data.total });
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams, searchQuery, selectedCategory, sortBy, businessType, userLocation, advancedFilters]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (category) params.set('category', category);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setAdvancedFilters({});
    setSearchParams({});
  };

  const handleAdvancedFiltersApply = (filters) => {
    setAdvancedFilters(filters);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Header */}
      <section className="pt-24 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Directory
              </p>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                {selectedCategory
                  ? (categories.find(c => c.id === selectedCategory)?.name || selectedCategory)
                  : 'All'} Businesses
              </h1>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMap(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  !showMap
                    ? 'bg-white text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white'
                }`}
                data-testid="list-view-button"
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => businessType !== 'online' && setShowMap(true)}
                disabled={businessType === 'online'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showMap
                    ? 'bg-white text-black'
                    : businessType === 'online'
                    ? 'bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white'
                }`}
                data-testid="map-view-button"
                title={businessType === 'online' ? 'Map not available for online businesses' : ''}
              >
                <Map className="w-4 h-4" />
                Map
              </button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search businesses..."
                  className="w-full h-12 bg-zinc-900/80 border border-zinc-800 text-white placeholder:text-zinc-600 rounded-lg px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/50 transition-all"
                  data-testid="listing-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-zinc-800 rounded-md flex items-center justify-center text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors"
                  data-testid="listing-search-button"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Local / Online Toggle */}
            <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-lg p-1">
              <button
                onClick={() => {
                  setBusinessType('local');
                  setShowMap(false);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  businessType === 'local'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-local"
              >
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Local</span>
              </button>
              <button
                onClick={() => setBusinessType('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  businessType === 'all'
                    ? 'bg-white text-black'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-all"
              >
                <span className="text-sm font-medium">All</span>
              </button>
              <button
                onClick={() => {
                  setBusinessType('online');
                  setShowMap(false); // Disable map for online mode
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  businessType === 'online'
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="filter-online"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">Online</span>
              </button>
            </div>

            {/* Advanced Filters Button */}
            <AdvancedFilters 
              onApply={handleAdvancedFiltersApply}
              activeFilters={advancedFilters}
            />

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center justify-center gap-2 h-12 px-4 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-400"
              data-testid="toggle-filters-button"
            >
              <Filter className="w-4 h-4" />
              Categories
            </button>
          </div>

          {/* Active Filters */}
          {(searchQuery || selectedCategory || Object.keys(advancedFilters).some(k => advancedFilters[k])) && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-zinc-500 text-sm">Active filters:</span>
              {selectedCategory && (
                <button
                  onClick={() => handleCategorySelect('')}
                  className="flex items-center gap-1 px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-300 hover:border-zinc-700"
                  data-testid="clear-category-filter"
                >
                  {categories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                  <X className="w-3 h-3" />
                </button>
              )}
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    const params = new URLSearchParams(searchParams);
                    params.delete('search');
                    setSearchParams(params);
                  }}
                  className="flex items-center gap-1 px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-300 hover:border-zinc-700"
                  data-testid="clear-search-filter"
                >
                  &quot;{searchQuery}&quot;
                  <X className="w-3 h-3" />
                </button>
              )}
              {advancedFilters.price_range && (
                <button
                  onClick={() => setAdvancedFilters(prev => ({ ...prev, price_range: null }))}
                  className="flex items-center gap-1 px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-300 hover:border-zinc-700"
                >
                  Price: {advancedFilters.price_range}
                  <X className="w-3 h-3" />
                </button>
              )}
              {advancedFilters.min_rating && (
                <button
                  onClick={() => setAdvancedFilters(prev => ({ ...prev, min_rating: null }))}
                  className="flex items-center gap-1 px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-300 hover:border-zinc-700"
                >
                  {advancedFilters.min_rating}+ Stars
                  <X className="w-3 h-3" />
                </button>
              )}
              {advancedFilters.open_now && (
                <button
                  onClick={() => setAdvancedFilters(prev => ({ ...prev, open_now: false }))}
                  className="flex items-center gap-1 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-sm text-green-400 hover:border-green-400"
                >
                  Open Now
                  <X className="w-3 h-3" />
                </button>
              )}
              {advancedFilters.verified_only && (
                <button
                  onClick={() => setAdvancedFilters(prev => ({ ...prev, verified_only: false }))}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-sm text-blue-400 hover:border-blue-400"
                >
                  Verified Only
                  <X className="w-3 h-3" />
                </button>
              )}
              {advancedFilters.has_reviews && (
                <button
                  onClick={() => setAdvancedFilters(prev => ({ ...prev, has_reviews: false }))}
                  className="flex items-center gap-1 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-sm text-purple-400 hover:border-purple-400"
                >
                  Has Reviews
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={clearFilters}
                className="text-zinc-500 text-sm hover:text-white transition-colors"
                data-testid="clear-all-filters"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden md:block w-64 shrink-0">
            <div className="sticky top-24">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Categories
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => handleCategorySelect('')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !selectedCategory
                      ? 'bg-zinc-900 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                  }`}
                  data-testid="category-all"
                >
                  All Categories
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    {category.name}
                  </button>
                ))}
              </nav>

              {/* Location Info */}
              {userLocation && (
                <div className="mt-8 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Navigation className="w-4 h-4 text-green-400" />
                    <span>Location enabled</span>
                  </div>
                  <p className="text-zinc-600 text-xs mt-1">
                    Showing distance to businesses
                  </p>
                </div>
              )}
            </div>
          </aside>

          {/* Mobile Filters Drawer */}
          {showFilters && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                className="absolute right-0 top-0 bottom-0 w-80 bg-[#0A0A0A] p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Filters</h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                  Categories
                </h4>
                <nav className="space-y-1">
                  <button
                    onClick={() => handleCategorySelect('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedCategory
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-zinc-900 text-white'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </nav>
              </motion.div>
            </div>
          )}

          {/* Business Grid */}
          <div className="flex-1">
            {/* Sort Options */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-zinc-400 text-sm">
                {loading ? 'Loading...' : `${pagination.total} businesses found`}
              </p>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-zinc-500" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-zinc-800">
                    <SelectItem value="default" className="text-white hover:bg-zinc-800">Default</SelectItem>
                    <SelectItem value="melanin_score" className="text-white hover:bg-zinc-800">
                      <span className="flex items-center gap-2">
                        <TrendingUp className="w-3 h-3 text-amber-400" />
                        Community Top Picks
                      </span>
                    </SelectItem>
                    <SelectItem value="rating" className="text-white hover:bg-zinc-800">Highest Rated</SelectItem>
                    <SelectItem value="newest" className="text-white hover:bg-zinc-800">Newest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* For You Section (Personalized Recommendations) */}
            {!loading && forYouBusinesses.length > 0 && !searchQuery && !selectedCategory && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <h2 className="font-semibold text-white">Recommended For You</h2>
                  <span className="text-zinc-500 text-sm">Based on your preferences</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {forYouBusinesses.slice(0, 6).map((biz, index) => (
                    <Link
                      key={biz.business_id}
                      to={`/businesses/${biz.business_id}`}
                      className="group block p-3 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-colors"
                      data-testid={`for-you-${biz.business_id}`}
                    >
                      <div className="aspect-square rounded-lg overflow-hidden mb-2">
                        <OptimizedImage 
                          src={biz.image_url}
                          fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                          alt={biz.name}
                          preset="thumb"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <h3 className="text-white font-medium text-sm truncate">{biz.name}</h3>
                      <p className="text-purple-400 text-xs truncate">{biz.recommendation_reason}</p>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Community Top Picks Section */}
            {!loading && topPicks.length > 0 && sortBy !== 'melanin_score' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                  <h2 className="font-semibold text-white">Community Top Picks</h2>
                  <span className="text-zinc-500 text-sm">Highest Melanin Score</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {topPicks.map((biz, index) => (
                    <Link
                      key={biz.business_id}
                      to={`/businesses/${biz.business_id}`}
                      className="block p-3 rounded-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 hover:border-amber-500/40 transition-colors"
                      data-testid={`top-pick-${biz.business_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                        <span className="text-amber-400 font-bold">{biz.melanin_score?.toFixed(1)}</span>
                      </div>
                      <h3 className="text-white font-medium text-sm truncate">{biz.name}</h3>
                      <p className="text-zinc-500 text-xs truncate">{biz.category}</p>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Featured Online Brands Carousel (Mixed Mode) */}
            {!loading && featuredOnline.length > 0 && businessType === 'all' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-purple-400" />
                    <h2 className="font-semibold text-white">Featured Online Brands</h2>
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                      Ships Nationwide
                    </span>
                  </div>
                  <button
                    onClick={() => setBusinessType('online')}
                    className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                    data-testid="view-all-online"
                  >
                    View All Online →
                  </button>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                  {featuredOnline.map((biz) => (
                    <Link
                      key={biz.business_id}
                      to={`/businesses/${biz.business_id}`}
                      className="flex-shrink-0 w-64 p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-colors"
                      data-testid={`featured-online-${biz.business_id}`}
                    >
                      {biz.image_url ? (
                        <OptimizedImage
                          src={biz.image_url}
                          alt={biz.name}
                          preset="card"
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      ) : (
                        <div className="w-full h-32 bg-zinc-900 rounded-lg mb-3 flex items-center justify-center">
                          <Globe className="w-8 h-8 text-purple-500/50" />
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          Online
                        </span>
                        {biz.melanin_score > 0 && (
                          <span className="flex items-center gap-1 text-amber-400 text-xs">
                            <img src={MELANIN_ICON} alt="" className="w-3 h-3" />
                            {biz.melanin_score.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <h3 className="text-white font-medium truncate">{biz.name}</h3>
                      <p className="text-zinc-500 text-xs truncate">{biz.service_area || 'Ships Nationwide'}</p>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/3] skeleton rounded-lg" />
                ))}
              </div>
            ) : businesses.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {businesses.map((business, index) => (
                    <motion.div
                      key={business.business_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                    >
                      <Link
                        to={`/businesses/${business.business_id}`}
                        className="block business-card rounded-lg overflow-hidden"
                        data-testid={`business-card-${business.business_id}`}
                      >
                        <div className="relative aspect-[4/3]">
                          <OptimizedImage
                            src={business.image_url}
                            fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                            alt={business.name}
                            preset="card"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          
                          <div className="absolute top-4 left-4 flex items-center gap-2">
                            {business.verification_status === 'verified' && (
                              <span className="verified-badge">
                                <Verified className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                            {business.badge_level && (
                              <BadgeDisplay level={business.badge_level} size="sm" />
                            )}
                            {business.distance !== undefined && (
                              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                                <Navigation className="w-3 h-3" />
                                {business.distance} mi
                              </span>
                            )}
                          </div>
                          
                          {/* Favorite Button */}
                          <div className="absolute top-4 right-4">
                            <FavoriteButton businessId={business.business_id} size="sm" />
                          </div>

                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="category-tag">{business.category}</p>
                              {business.melanin_score > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">
                                  <img src={MELANIN_ICON} alt="" className="w-3 h-3" />
                                  {business.melanin_score.toFixed(1)}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-1">
                              {business.name}
                            </h3>
                            
                            {/* Rating */}
                            {business.review_count > 0 && (
                              <div className="flex items-center gap-2 mb-2">
                                {renderStars(Math.round(business.average_rating))}
                                <span className="text-zinc-400 text-xs">
                                  ({business.review_count})
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-1 text-zinc-400 text-sm">
                              {business.is_online_only ? (
                                <>
                                  <Globe className="w-3 h-3 text-purple-400" />
                                  <span className="truncate text-purple-400">
                                    {business.service_area || 'Online'}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <MapPin className="w-3 h-3" />
                                  <span className="truncate">
                                    {business.city || business.address?.split(',')[0] || 'Location'}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed hover:border-zinc-700 hover:text-white transition-colors"
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    <span className="px-4 font-mono text-sm text-zinc-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed hover:border-zinc-700 hover:text-white transition-colors"
                      data-testid="pagination-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No businesses found</h3>
                <p className="text-zinc-500 mb-6">No businesses match your criteria. Know one that should be listed?</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={clearFilters}
                    className="btn-secondary"
                    data-testid="clear-filters-empty"
                  >
                    Clear Filters
                  </button>
                  <Link
                    to={`/bounties?category=${selectedCategory || ''}&search=${searchQuery || ''}`}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="create-bounty-from-search"
                  >
                    <Target className="w-4 h-4" />
                    Create a Bounty
                  </Link>
                </div>
                <p className="text-zinc-600 text-sm mt-4">
                  Offer a reward for someone to find and list the business you&apos;re looking for
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Map View Overlay */}
      {showMap && (
        <MapView 
          category={selectedCategory} 
          onClose={() => setShowMap(false)} 
        />
      )}
    </div>
  );
}
